import { useCallback, useEffect, useMemo, useState } from 'react';

const SUB_MODAL_ANIMATION_MS = 300;

let nextSubModalId = 0;
const newSubModalId = () => `sub-modal-${++nextSubModalId}`;

// Sheets form a small stack: opening normally replaces the top (in-place update
// pattern), `stack: true` pushes so closing restores the sheet underneath
// (e.g. a confirm layered over an open sheet).
export const useSubModalStack = (controlledSubModal) => {
  const [subModalStack, setSubModalStack] = useState([]);
  const internalSubModal = subModalStack[subModalStack.length - 1] ?? null;
  const subModal = controlledSubModal !== undefined ? controlledSubModal : internalSubModal;

  // Memoized so it can be an effect dependency — a fresh array every render
  // would re-run the effect forever in the controlled case.
  const stackForRender = useMemo(
    () =>
      controlledSubModal !== undefined
        ? controlledSubModal
          ? [controlledSubModal]
          : []
        : subModalStack,
    [controlledSubModal, subModalStack]
  );

  const [activeSubModal, setActiveSubModal] = useState(subModal || null);
  const [activeStack, setActiveStack] = useState(stackForRender);
  const [subModalVisible, setSubModalVisible] = useState(!!subModal);

  // Keyed on the stack itself, not just its top entry: removing a *lower* sheet
  // leaves the top unchanged, and that still has to drop the closed entry from
  // the rendered stack.
  useEffect(() => {
    if (subModal) {
      setActiveSubModal(subModal);
      setActiveStack(stackForRender);
      const t = setTimeout(() => setSubModalVisible(true), 20);
      return () => clearTimeout(t);
    } else {
      setSubModalVisible(false);
      const t = setTimeout(() => {
        setActiveSubModal(null);
        setActiveStack([]);
      }, SUB_MODAL_ANIMATION_MS);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subModal, stackForRender]);

  // Entries carry an identity so a sheet closes itself rather than whatever
  // happens to be on top — sibling sheets (not just nested ones) can close in
  // any order, and a positional pop would remove the wrong entry.
  //
  // `slotKey` is the React key of the rendered slot and is separate from `id`: an
  // in-place replacement (plain open over an existing top) inherits the slot so the
  // mounted content keeps its state, while a push gets its own — so removing a lower
  // entry can never hand its mounted content to the sheet that shifts into its index.
  const openSubModal = useCallback((config) => {
    const id = config?.id != null ? String(config.id) : newSubModalId();
    setSubModalStack((prev) => {
      const top = prev[prev.length - 1];
      const slotKey = !config?.stack && top ? top.slotKey : newSubModalId();
      const entry = { ...config, id, slotKey };
      return config?.stack ? [...prev, entry] : [...prev.slice(0, -1), entry];
    });
    return id;
  }, []);

  const closeSubModal = useCallback(
    (id) =>
      setSubModalStack((prev) =>
        id ? prev.filter((entry) => entry.id !== id) : prev.slice(0, -1)
      ),
    []
  );

  return { activeSubModal, activeStack, subModalVisible, openSubModal, closeSubModal };
};
